/**
 * Browser client for the agent-chat sidecar's SSE contract (Managed Agents v2 —
 * Menu C). POSTs a message to the sidecar's `chat_url` (Authorization: Bearer
 * <chat-token>), reads the streamed turn via fetch + ReadableStream, and invokes
 * `onEvent` with each normalized event. Resolves when the turn ends (a `done` or
 * `error` frame, or the stream closing).
 *
 * We consume the sidecar's own SSE frames (see agent-chat/src/lib/sse.ts) rather
 * than binding to a specific AI-SDK stream version — both ends are ours.
 */

export type ChatEvent =
  | { type: 'session'; sessionId: string }
  | { type: 'text'; text: string }
  | { type: 'delta'; text: string }
  | { type: 'tool'; name: string; input: unknown }
  | { type: 'tool_result'; toolUseId: string | null }
  | { type: 'status'; status: string }
  | { type: 'done'; stopReason: string | null }
  | ChatErrorEvent;

/**
 * A failed turn. `message` stays the human string every consumer already reads;
 * `code` and `canRetry` carry the sidecar's machine-readable signal alongside it
 * (`can_retry` is sent precisely so the browser need not match on code strings).
 * Both are absent when the payload didn't carry them — an older sidecar or a
 * non-JSON body — so the message stays the fallback signal.
 */
export interface ChatErrorEvent {
  type: 'error';
  message: string;
  code?: string;
  canRetry?: boolean;
}

/** Build an `error` event, keeping the machine-readable parts of `payload`. */
function errorEvent(message: string, payload: any): ChatErrorEvent {
  const event: ChatErrorEvent = { type: 'error', message };
  // An SSE error frame names the code in `code`; an HTTP error body in `error`.
  const code = payload?.code ?? payload?.error;
  if (typeof code === 'string' && code) event.code = code;
  if (payload?.can_retry === true) event.canRetry = true;
  return event;
}

export interface StreamChatOptions {
  chatUrl: string;
  token: string;
  message: string;
  /** Resume an existing conversation (the sessionId from the first `session` event). */
  sessionId?: string;
  onEvent: (event: ChatEvent) => void;
  signal?: AbortSignal;
  /** Injected for tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

/** Map one SSE frame (event name + JSON data) to a normalized ChatEvent. */
export function frameToEvent(event: string, data: string): ChatEvent | null {
  let parsed: any;
  try {
    parsed = data ? JSON.parse(data) : {};
  } catch {
    return null;
  }
  switch (event) {
    case 'session':
      return { type: 'session', sessionId: String(parsed.session_id ?? '') };
    case 'text':
      return { type: 'text', text: String(parsed.text ?? '') };
    case 'delta':
      return { type: 'delta', text: String(parsed.text ?? '') };
    case 'tool':
      return { type: 'tool', name: String(parsed.name ?? 'tool'), input: parsed.input ?? null };
    case 'tool_result':
      return { type: 'tool_result', toolUseId: parsed.tool_use_id ?? null };
    case 'status':
      return { type: 'status', status: String(parsed.status ?? '') };
    case 'done':
      return { type: 'done', stopReason: parsed.stop_reason ?? null };
    case 'error':
      return errorEvent(String(parsed.message ?? 'chat error'), parsed);
    default:
      return null;
  }
}

/** Parse one raw SSE frame block into its `event` and joined `data`. */
export function parseFrame(frame: string): { event: string; data: string } | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const raw of frame.split('\n')) {
    const line = raw.replace(/\r$/, '');
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

/**
 * Open the turn and pump events. Rejects only on a transport/HTTP failure
 * (an in-band `error` frame is delivered via onEvent, not thrown).
 */
export async function streamChatTurn(opts: StreamChatOptions): Promise<void> {
  const doFetch = opts.fetchImpl ?? fetch;
  const res = await doFetch(opts.chatUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${opts.token}`,
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      message: opts.message,
      ...(opts.sessionId ? { session_id: opts.sessionId } : {}),
    }),
    signal: opts.signal,
  });

  if (!res.ok || !res.body) {
    // Try to surface a JSON error body from central-api/sidecar.
    let detail = `Chat request failed (${res.status}).`;
    let body: any = null;
    try {
      body = await res.json();
      if (body?.error || body?.message) detail = String(body.message || body.error);
    } catch {
      /* non-JSON */
    }
    opts.onEvent(errorEvent(detail, body));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseFrame(frame);
        if (!parsed) continue;
        const ev = frameToEvent(parsed.event, parsed.data);
        if (ev) {
          opts.onEvent(ev);
          if (ev.type === 'done' || ev.type === 'error') return;
        }
      }
    }
    // Flush a trailing frame with no terminating blank line.
    const parsed = parseFrame(buffer);
    if (parsed) {
      const ev = frameToEvent(parsed.event, parsed.data);
      if (ev) opts.onEvent(ev);
    }
  } finally {
    reader.releaseLock();
  }
}
